import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  X, Upload, Loader2, CheckCircle, AlertTriangle, FileText,
  ServerCog, Link2,
} from 'lucide-react'
import { apiGet, apiPut } from '@/lib/api'
import { cn } from '@/lib/utils'

interface Site {
  id: string
  name: string
  description: string | null
}

interface PropertyUploadModalProps {
  propertyId: string
  propertyName: string
  onClose: () => void
  onUploaded?: () => void
}

export function PropertyUploadModal({ propertyId, propertyName, onClose, onUploaded }: PropertyUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [results, setResults] = useState<{ name: string; ok: boolean; error?: string }[]>([])
  const [dragOver, setDragOver] = useState(false)

  // Fetch available DocFlow sites + the currently mapped site for this property
  const { data: sites, isLoading: sitesLoading, error: sitesError } = useQuery({
    queryKey: ['docflow-sites'],
    queryFn: () => apiGet<Site[]>('/docflow/sites'),
  })

  const { data: mapping, refetch: refetchMapping } = useQuery({
    queryKey: ['docflow-property-site', propertyId],
    queryFn: () => apiGet<{ propertyId: string; siteId: string | null }>(`/docflow/property-site/${propertyId}`),
  })

  const [selectedSiteId, setSelectedSiteId] = useState<string>('')

  useEffect(() => {
    if (mapping?.siteId && !selectedSiteId) setSelectedSiteId(mapping.siteId)
  }, [mapping?.siteId])

  // If no mapping exists and there's exactly one site, default to it
  useEffect(() => {
    if (!selectedSiteId && !mapping?.siteId && sites && sites.length === 1) {
      setSelectedSiteId(sites[0].id)
    }
  }, [sites, mapping?.siteId])

  const saveMappingMutation = useMutation({
    mutationFn: (siteId: string) => apiPut(`/docflow/property-site/${propertyId}`, { siteId }),
    onSuccess: () => refetchMapping(),
  })

  function addFiles(list: FileList | File[]) {
    const arr = Array.from(list)
    setFiles((prev) => [...prev, ...arr])
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleUpload() {
    if (files.length === 0 || uploading) return
    setUploading(true)
    setResults([])
    const token = JSON.parse(localStorage.getItem('estatekey-auth') ?? '{}')?.state?.accessToken
    const out: { name: string; ok: boolean; error?: string }[] = []

    for (const file of files) {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('title', file.name)
      fd.append('propertyId', propertyId)
      if (selectedSiteId) fd.append('siteId', selectedSiteId)

      try {
        const res = await fetch('/api/v1/docflow/upload', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd,
        })
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: 'Upload failed' }))
          out.push({ name: file.name, ok: false, error: err.error ?? `HTTP ${res.status}` })
        } else {
          out.push({ name: file.name, ok: true })
        }
      } catch (e: any) {
        out.push({ name: file.name, ok: false, error: e?.message ?? 'Network error' })
      }
      setResults([...out])
    }

    setUploading(false)
    if (onUploaded) onUploaded()
    if (out.every((r) => r.ok)) setFiles([])
  }

  const selectedSite = sites?.find((s) => s.id === selectedSiteId)
  const isMapped = mapping?.siteId === selectedSiteId && !!selectedSiteId
  const canSaveMapping = !!selectedSiteId && !isMapped

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-semibold text-navy-800 flex items-center gap-2">
              <Upload className="w-4 h-4" /> Upload to {propertyName}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Documents upload to this property's DocFlow site.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-navy-800 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Site selector */}
          <div>
            <label className="text-xs font-medium text-navy-800 flex items-center gap-1.5 mb-1.5">
              <ServerCog className="w-3.5 h-3.5 text-gray-400" /> DocFlow site
            </label>
            {sitesLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading sites...
              </div>
            ) : sitesError ? (
              <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded-lg p-2">
                <AlertTriangle className="w-3.5 h-3.5" />
                DocFlow not connected — check Settings.
              </div>
            ) : !sites?.length ? (
              <p className="text-xs text-gray-500">No DocFlow sites available.</p>
            ) : (
              <>
                <select
                  value={selectedSiteId}
                  onChange={(e) => setSelectedSiteId(e.target.value)}
                  className="input w-full py-1.5 text-sm"
                >
                  <option value="">— Default (first connected site) —</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}{s.id === mapping?.siteId ? '  ★ mapped' : ''}
                    </option>
                  ))}
                </select>
                {selectedSite && (
                  <p className="text-xs text-gray-500 mt-1">
                    Uploading into <b>{selectedSite.name}</b>
                    {selectedSite.description ? ` — ${selectedSite.description}` : ''}.
                  </p>
                )}
                {canSaveMapping && (
                  <button
                    onClick={() => saveMappingMutation.mutate(selectedSiteId)}
                    disabled={saveMappingMutation.isPending}
                    className="mt-2 flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 font-medium"
                  >
                    {saveMappingMutation.isPending ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Link2 className="w-3 h-3" />
                    )}
                    Remember this site for {propertyName}
                  </button>
                )}
                {isMapped && (
                  <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> This site is mapped to {propertyName}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Dropzone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragOver(false)
              if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files)
            }}
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
              dragOver ? 'border-purple-400 bg-purple-50' : 'border-gray-200'
            )}
          >
            <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-navy-800">Drop files here or</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn-secondary mt-2 text-xs py-1.5 px-3"
            >
              Choose files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { if (e.target.files) addFiles(e.target.files); if (fileInputRef.current) fileInputRef.current.value = '' }}
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <ul className="space-y-1.5 max-h-48 overflow-y-auto">
              {files.map((f, i) => {
                const r = results[i]
                return (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center gap-2 p-2 rounded-lg border border-gray-100 bg-gray-50"
                  >
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-navy-800 truncate">{f.name}</p>
                      <p className="text-xs text-gray-400">
                        {(f.size / 1024).toFixed(1)} KB
                        {r?.ok && <span className="ml-2 text-green-700">• Uploaded</span>}
                        {r && !r.ok && <span className="ml-2 text-red-700">• {r.error}</span>}
                      </p>
                    </div>
                    {!uploading && !r?.ok && (
                      <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-600 p-1">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {r?.ok && <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />}
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">
            Close
          </button>
          <button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="btn-primary text-sm flex items-center gap-2"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : `Upload ${files.length || ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}
